import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { DateRange, PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import {
  AccountType,
  BulkSettlementCreateFor,
  InvoiceCategoryFilter,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import AutocompleteAccountsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteAccountsTypeahead';
import AutocompleteDriversTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteDriversTypeahead';
import { SingleInputDateRangePicker } from '~components/FormFields/SingleInputDateRangePicker';

import {
  getEarliestBulkSettlementDate,
  getLatestBulkSettlementDate,
} from './bulkSettlementDateRange';
import {
  getBulkSettlementEntityTypeOption,
  getBulkSettlementEntityTypesOfCategory,
} from './bulkSettlementEntityTypes';

const bulkSettlementSchema = yup.object().shape({
  dateRange: yup.array().of(yup.mixed<Dayjs>()).length(2).required(),
  entityType: yup
    .array()
    .of(
      yup.object().shape({
        value: yup
          .mixed<BulkSettlementCreateFor>()
          .oneOf(Object.values(BulkSettlementCreateFor))
          .required(),
        label: yup.string().required(),
      }),
    )
    .min(1)
    .required(),
  customerAccountIds: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required(),
      }),
    )
    .optional()
    .default([]),
  vendorAccountIds: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required(),
      }),
    )
    .optional()
    .default([]),
  driverIds: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required(),
      }),
    )
    .optional()
    .default([]),
});

export type BulkSettlementDTO = yup.InferType<typeof bulkSettlementSchema>;

const setBulkSettlementSchemaDefaultValues = () => {
  return {
    dateRange: [null, null] as DateRange<Dayjs>,
    entityType: [],
    customerAccountIds: [],
    vendorAccountIds: [],
    driverIds: [],
  } as unknown as BulkSettlementDTO;
};

export type BulkFormFieldsHandler = {
  onSubmit?: () => Promise<BulkSettlementDTO>;
};

// Custom shortcuts for 60-day restricted date range picker
const bulkSettlementShortcuts: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: t('date.yesterday'),
    getValue: () => {
      const today = dayjs().tz();
      const yesterday = today.subtract(1, 'day');
      return [yesterday.startOf('day'), yesterday.endOf('day')];
    },
  },
  {
    label: t('date.today'),
    getValue: () => {
      const today = dayjs().tz();
      return [today.startOf('day'), today.endOf('day')];
    },
  },
  {
    label: t('date.last_seven_days'),
    getValue: () => {
      const today = dayjs().tz();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: t('date.last_week'),
    getValue: () => {
      const today = dayjs().tz();
      const prevWeek = today.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    },
  },
  { label: t('actions.reset'), getValue: () => [null, null] },
];

type EntityTypeOption = { value: BulkSettlementCreateFor; label: string };

interface BulkCreateSettlementFormFieldsProps {
  category: InvoiceCategoryFilter;
  companyId: string;
  initialEntityTypes?: EntityTypeOption[];
  entityTypeLocked?: boolean;
  initialDateRange?: [Dayjs, Dayjs];
}

export const BulkCreateSettlementFormFields = forwardRef<
  BulkFormFieldsHandler,
  BulkCreateSettlementFormFieldsProps
>(function BulkCreateSettlementFormFields(
  { category, companyId, initialEntityTypes, entityTypeLocked, initialDateRange },
  ref,
) {
  const entityTypeList = useMemo(() => {
    if (initialEntityTypes && initialEntityTypes.length > 0) {
      return initialEntityTypes;
    }

    return getBulkSettlementEntityTypesOfCategory(category).map(
      getBulkSettlementEntityTypeOption,
    );
  }, [category, initialEntityTypes]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm<BulkSettlementDTO>({
    resolver: yupResolver(bulkSettlementSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setBulkSettlementSchemaDefaultValues(),
  });

  const selectedEntityTypes = watch('entityType');

  const selectedEntityTypeValues = useMemo(() => {
    return (selectedEntityTypes ?? []).map((entity) => entity.value);
  }, [selectedEntityTypes]);

  // Pre-select entity types when initialEntityTypes is provided
  useEffect(() => {
    if (initialEntityTypes && initialEntityTypes.length > 0) {
      setValue('entityType', initialEntityTypes);
    }
  }, [initialEntityTypes, setValue]);

  // Pre-populate date range from page filters when provided
  useEffect(() => {
    if (initialDateRange) {
      setValue(
        'dateRange',
        initialDateRange as unknown as BulkSettlementDTO['dateRange'],
      );
    }
  }, [initialDateRange, setValue]);

  // Clear specific ID fields when their entity type is deselected
  useEffect(() => {
    if (!selectedEntityTypeValues.includes(BulkSettlementCreateFor.CUSTOMERS)) {
      setValue('customerAccountIds', []);
    }
    if (!selectedEntityTypeValues.includes(BulkSettlementCreateFor.VENDORS)) {
      setValue('vendorAccountIds', []);
    }
    if (!selectedEntityTypeValues.includes(BulkSettlementCreateFor.DRIVERS)) {
      setValue('driverIds', []);
    }
  }, [selectedEntityTypeValues, setValue]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit: () => {
        return new Promise<BulkSettlementDTO>((resolve, reject) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            (errors) => {
              reject(errors);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
          }}
        >
          {t('form_fields.date_range')}
        </Typography>

        <SingleInputDateRangePicker
          control={control}
          errors={errors}
          name="dateRange"
          isRequired
          minDate={getEarliestBulkSettlementDate()}
          maxDate={getLatestBulkSettlementDate()}
          customShortcuts={bulkSettlementShortcuts}
        />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {t('approvals.driver_pay.bulk_create_settlement_dialog.date_range_restriction')}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
          }}
        >
          {t(
            'approvals.driver_pay.bulk_create_settlement_dialog.select_entity_type_for_settlements',
          )}
        </Typography>

        <AutocompleteFormField
          control={control}
          errors={errors}
          name="entityType"
          multiple
          list={entityTypeList}
          getValue={(item: EntityTypeOption) => item.value}
          getLabel={(item: EntityTypeOption) => item.label}
          isRequired
          disabled={entityTypeLocked}
        />
      </Box>
      {selectedEntityTypeValues.includes(BulkSettlementCreateFor.CUSTOMERS) && (
        <Box>
          <AutocompleteAccountsTypeahead
            control={control}
            name="customerAccountIds"
            companyId={companyId}
            accountTypes={[AccountType.CUSTOMER]}
            isMultiple
            label={t(
              'approvals.driver_pay.bulk_create_settlement_dialog.specific_customers',
            )}
          />
        </Box>
      )}
      {selectedEntityTypeValues.includes(BulkSettlementCreateFor.VENDORS) && (
        <Box>
          <AutocompleteAccountsTypeahead
            control={control}
            name="vendorAccountIds"
            companyId={companyId}
            accountTypes={[AccountType.VENDOR]}
            isMultiple
            label={t(
              'approvals.driver_pay.bulk_create_settlement_dialog.specific_vendors',
            )}
          />
        </Box>
      )}
      {selectedEntityTypeValues.includes(BulkSettlementCreateFor.DRIVERS) && (
        <Box>
          <AutocompleteDriversTypeahead
            control={control}
            name="driverIds"
            companyId={companyId}
            isMultiple
            label={t(
              'approvals.driver_pay.bulk_create_settlement_dialog.specific_drivers',
            )}
          />
        </Box>
      )}
      <Divider sx={{ mb: 2 }} />
    </Box>
  );
});
